import { coverOf } from "./cover";

/** How many of the most popular items the shelf draws from, so it rotates without ever showing a weak pick */
const POOL_SIZE = 8;

const DAY_MS = 86_400_000;

/**
 * How popular a collection is on the chart: each of its podcasts on the chart counts for the places below the
 * bottom of it, so a collection scores for having podcasts high up and for having many of them.
 */
export const collectionScore = (collection: Collection, chart: ToplistEntry[]): number => {
    if (chart.length === 0) return 0;
    const places = new Map<number, number>();
    let depth = 0;
    for (const entry of chart) {
        // The chart's own podcast_id is not the site's id for the podcast: the podcast it carries is
        if (entry.podcast) places.set(entry.podcast.id, entry.pos);
        depth = Math.max(depth, entry.pos + 1);
    }
    let score = 0;
    for (const podcast of collection.podcasts) {
        const pos = places.get(podcast.id);
        if (pos !== undefined) score += depth - pos;
    }
    return score;
};

/** `count` items from `items`, starting at an offset that changes daily. */
export const rotate = <T>(items: T[], count: number, now = Date.now()): T[] => {
    if (items.length <= count) return items;
    const day = Math.floor(now / DAY_MS);
    return Array.from({ length: count }, (_, i) => items[(day + i) % items.length]);
};

/** Items with a cover photo, in the order they came, then the rest. */
const coversFirst = <T extends object>(items: T[]): T[] => [
    ...items.filter((item) => coverOf(item)),
    ...items.filter((item) => !coverOf(item)),
];

/**
 * The collections for the front page: the most popular on the chart, rotated daily within the top few, those with
 * a cover photo first. Without a chart there is no popularity to go by, so all of them take part in the rotation.
 */
export const featuredCollections = (
    collections: Collection[],
    chart: ToplistEntry[],
    count: number,
    now = Date.now()
): Collection[] => {
    const ranked = collections
        .map((collection) => ({ collection, score: collectionScore(collection, chart) }))
        .sort((a, b) => b.score - a.score || a.collection.name.localeCompare(b.collection.name, "sv"))
        .map((item) => item.collection);
    const pool = chart.length > 0 ? ranked.slice(0, Math.max(POOL_SIZE, count)) : ranked;
    return coversFirst(rotate(pool, count, now));
};

/**
 * The focus areas for the front page. The list says nothing about which are popular, so those with a cover photo
 * come first (they are the ones chosen to be shown), each group rotated daily.
 */
export const featuredSubjects = (subjects: Subject[], count: number, now = Date.now()): Subject[] => {
    const covered = subjects.filter((subject) => coverOf(subject));
    const rest = subjects.filter((subject) => !coverOf(subject));
    return [...rotate(covered, count, now), ...rotate(rest, count, now)].slice(0, count);
};
